import { profileByTenant, segmentByTenant } from '../graphql/queries';
import { fetchAllPages } from './graphqlPaging';

/**
 * Helpers for detecting records that already share a name within a tenant.
 *
 * The motivating problem: a user accidentally creates two segments (or two
 * profiles) with the same name, then applies the wrong one to a campaign.
 * These checks let a create/clone/rename flow warn the user before saving.
 *
 * Each `check*NameExists` function returns:
 *   { exists: false, matches: [] }
 * or
 *   { exists: true, matches: Array<{ id, name }> }
 *
 * `excludeId` lets an edit flow ignore the record being edited so renaming a
 * record to its own current name never triggers a false positive.
 *
 * These reuse the generated "byTenant" queries (same approach as checkUsage.js)
 * rather than defining new custom query strings, and page through every result
 * so a duplicate sitting beyond the first page is still found.
 */

export async function checkSegmentNameExists({ tenantId, name, excludeId }) {
	if (!tenantId || !name || !name.trim()) {
		return { exists: false, matches: [] };
	}

	const segments = await getAllTenantItems(
		segmentByTenant,
		'segmentByTenant',
		tenantId,
	);

	return findNameMatches(segments, name, excludeId);
}

export async function checkProfileNameExists({ tenantId, name, excludeId }) {
	if (!tenantId || !name || !name.trim()) {
		return { exists: false, matches: [] };
	}

	const profiles = await getAllTenantItems(
		profileByTenant,
		'profileByTenant',
		tenantId,
	);

	return findNameMatches(profiles, name, excludeId);
}

/** Helper Functions */

// Case-insensitive name comparison so "My Segment" and "my segment" are treated
// as duplicates. Excludes the record being edited when excludeId is provided.
function findNameMatches(items, name, excludeId) {
	const target = name.trim().toLowerCase();
	const matches = items
		.filter((item) => item.id !== excludeId)
		.filter((item) => (item.name || '').trim().toLowerCase() === target)
		.map((item) => ({ id: item.id, name: item.name }));

	return { exists: matches.length > 0, matches };
}

// Convenience wrapper over the shared pager for the tenant "byTenant" queries.
async function getAllTenantItems(query, resultKey, tenantId) {
	return fetchAllPages({ query, resultKey, variables: { tenant: tenantId } });
}
